// src/seo/blog/esaLetterCluster.ts
//
// PAWTENANT-BLOG-PUBLISHING-SEO-CLUSTER-001
//
// ONE record per article in the ESA-letter blog cluster, and it is the ONLY
// place the cluster's FAQ copy, hero image and schema metadata exist.
//
// WHY A SHARED MODULE RATHER THAN PER-PAGE CONSTANTS
// The defect this design exists to make impossible is a FAQPage block whose
// questions do not match the accordion a human reads. Every page renders its
// visible FAQ from `faqs` below, and every JSON-LD emitter — the build-time
// head injection in src/prerender/entry.tsx and the client-side
// <BlogArticleSchema> — reads the SAME array. Visible/schema divergence would
// require editing this file in a way that changes both at once, which is the
// property scripts/check-blog-esa-cluster-seo.mjs asserts.
//
// EDITORIAL / LEGAL CONTRACT (verified 2026-09-22, see the guard for the
// machine-checked half):
//   * HUD withdrew Notices FHEO-2020-01 and FHEO-2013-01 on SEPTEMBER 17, 2025.
//     A May 2026 date for the withdrawal is wrong and is blocked by the guard.
//   * HUD's MAY 22, 2026 FHEO enforcement memorandum directs FHEO to find
//     reasonable cause only where the animal is individually trained to perform
//     work or tasks related to the disability. It is an internal ENFORCEMENT
//     policy: it did not amend 42 U.S.C. 3604(f) or 24 C.F.R. Part 100, it
//     preserves private rights of action, and it does not touch state or local
//     law. Never describe it as a change to the statute.
//   * No federal law sets a 12-month expiration for ESA documentation. Twelve
//     months is a housing-provider RECENCY practice (and, in Arkansas, a state
//     statutory annual-review duty on the provider). Never call it federal.
//   * HIPAA binds health care providers, not landlords. What limits a housing
//     provider is fair-housing law, not HIPAA.
//   * Nothing here promises approval, fee waivers, or any legal outcome.

export interface ClusterFaq {
  q: string;
  a: string;
}

export interface ClusterArticle {
  /** Canonical route path, e.g. "/blog/esa-letter-requirements". */
  route: string;
  /** The page's single <h1>. */
  h1: string;
  /** <title> — also the CORE_PAGE_META title, so the two cannot drift. */
  metaTitle: string;
  /** <meta name="description"> — also the CORE_PAGE_META description. */
  metaDescription: string;
  /** Final BreadcrumbList crumb name. */
  breadcrumb: string;
  /** Schema headline. Kept separate from h1 only where h1 carries markup. */
  headline: string;
  /** Schema description — the long-form summary, distinct from metaDescription. */
  schemaDescription: string;
  /** Site-root-relative path to the 1200x630 hero/social image. */
  heroImage: string;
  heroAlt: string;
  heroWidth: number;
  heroHeight: number;
  datePublished: string;
  dateModified: string;
  articleSection: string;
  keywords: string[];
  faqs: ClusterFaq[];
}

export const BASE_URL = "https://pawtenant.com";

const HERO_W = 1200;
const HERO_H = 630;

/** Every article in the cluster, keyed by canonical route path. */
export const ESA_LETTER_CLUSTER: Record<string, ClusterArticle> = {
  "/blog/can-primary-care-doctor-write-esa-letter": {
    route: "/blog/can-primary-care-doctor-write-esa-letter",
    h1: "Can a Primary Care Doctor Write an ESA Letter?",
    metaTitle: "Can a Primary Care Doctor Write an ESA Letter? | PawTenant",
    metaDescription:
      "A primary care doctor can write an ESA letter, but many decline. Who may issue housing documentation, what it must contain, and what to do if yours says no.",
    breadcrumb: "Can a Primary Care Doctor Write an ESA Letter?",
    headline: "Can a Primary Care Doctor Write an ESA Letter?",
    schemaDescription:
      "Whether a primary care provider can issue emotional support animal documentation for housing, why many physicians decline, which licensed professionals housing providers accept, the state rules that require an established treating relationship, and how to ask your doctor.",
    heroImage: "/assets/blog/esa-cluster/primary-care-doctor-esa-letter-1200x630.webp",
    heroAlt:
      "Pet owner at home with a small dog during a telehealth appointment on a laptop",
    heroWidth: HERO_W,
    heroHeight: HERO_H,
    datePublished: "2026-09-22",
    dateModified: "2026-09-22",
    articleSection: "ESA Letters",
    keywords: [
      "can a primary care doctor write an ESA letter",
      "ESA letter from doctor",
      "who can write an ESA letter",
      "doctor letter for emotional support animal",
      "primary care provider ESA",
    ],
    faqs: [
      {
        q: "Can a primary care doctor write an ESA letter?",
        a: "Yes. Nothing in the Fair Housing Act limits emotional support animal documentation to mental health specialists, so a primary care physician, an internist, a physician assistant or a nurse practitioner may write one — provided they hold an active licence in the state where you live, they are working within their own scope of practice, and they have genuinely evaluated you. A doctor is never obliged to write one, and many decline for the reasons set out in this guide.",
      },
      {
        q: "Why do so many primary care doctors refuse to write ESA letters?",
        a: "Four reasons come up repeatedly. Some physicians do not consider assessing a psychiatric disability to be within their comfort zone even though it is within their licence. Some work for a health system whose internal policy routes every assistance-animal request to behavioural health. Some are wary of being contacted by a property manager about a document they signed. And in several states a provider is legally barred from issuing the documentation until a treating relationship has existed for a set period, which rules out a request made at a first appointment.",
      },
      {
        q: "Does my doctor have to write my diagnosis in the letter?",
        a: "No, and most will not. The documentation needs to establish that you have a disability as fair-housing law defines it and to explain how the animal relates to it. Naming the condition is not required. A housing provider is not entitled to your medical records or to the nature or severity of your disability. That limit comes from fair-housing law, not from HIPAA — HIPAA governs what your clinician and health plan may disclose, and it does not by itself regulate what a landlord is allowed to ask you.",
      },
      {
        q: "Is a telehealth ESA letter from an online provider valid?",
        a: "It can be, when the clinician is licensed in your state and performs a real clinical evaluation rather than scoring a web form. Telehealth is an accepted way to deliver care in every state. What makes online documentation weak is the absence of an evaluation, not the absence of a waiting room — and state rules still apply, so a California or Montana clinician must have had a client relationship with you for at least 30 days first, and an out-of-state practitioner writing for a Florida tenant must have provided in-person care on at least one occasion.",
      },
      {
        q: "What should I do if my primary care doctor says no?",
        a: "Ask for a referral rather than arguing. A short, specific request works best: acknowledge that assistance-animal documentation may sit outside their usual practice, and ask who inside the network handles housing accommodation requests. You can also work with a licensed mental health professional directly, in person or by telehealth. A refusal from one clinician is not a determination that you do not qualify; it usually means that clinician does not write this kind of documentation.",
      },
      {
        q: "Can a pediatrician write the letter for my child's emotional support animal?",
        a: "A pediatrician, child psychologist or family physician can write documentation for a minor on the same basis as for an adult: an active licence in the family's state, a real evaluation, and a clear link between the child's disability and the animal. The accommodation request itself is normally made by the parent or guardian who is a party to the lease.",
      },
      {
        q: "Will a doctor's ESA letter get my animal into the aircraft cabin for free?",
        a: "No. Under the Department of Transportation's December 2020 final rule, effective 11 January 2021, airlines are no longer required to treat emotional support animals as service animals and may handle them as pets. Only a dog individually trained to perform tasks for a disability — a service animal, including a psychiatric service dog — keeps guaranteed cabin access. Housing documentation has no effect on air travel.",
      },
    ],
  },

  "/blog/esa-letter-requirements": {
    route: "/blog/esa-letter-requirements",
    h1: "ESA Letter Requirements: What Makes It Valid in 2026",
    metaTitle: "ESA Letter Requirements: What Makes It Valid in 2026",
    metaDescription:
      "The 10 elements a valid ESA letter must contain to be accepted by landlords, what makes one invalid, the state rules, and what HUD's 2025–2026 shifts changed.",
    breadcrumb: "ESA Letter Requirements",
    headline: "ESA Letter Requirements: What Makes It Valid in 2026",
    schemaDescription:
      "The 10 required elements of a valid ESA letter, what makes a letter invalid even when it looks real, state-specific rules, and what changed after HUD's 2025–2026 policy shifts.",
    heroImage: "/assets/blog/esa-cluster/esa-letter-requirements-1200x630.webp",
    heroAlt:
      "Renter reading ESA letter paperwork at a kitchen table with a small brown dog on his lap",
    heroWidth: HERO_W,
    heroHeight: HERO_H,
    datePublished: "2026-06-18",
    dateModified: "2026-09-22",
    articleSection: "ESA Letters",
    keywords: [
      "ESA letter requirements",
      "valid ESA letter requirements",
      "what must an ESA letter include",
      "FHA ESA letter",
      "emotional support animal letter requirements",
    ],
    faqs: [
      {
        q: "What must an ESA letter include to be valid in 2026?",
        a: "A valid ESA letter generally includes 10 elements: professional letterhead, the clinician's full name, license type and number, state of licensure, practice contact information, the date of the provider relationship/evaluation, the patient's full name, a statement confirming a qualifying disability, a disability-animal nexus statement explaining how the animal helps, and the clinician's signature with issue date. Missing the license number or the nexus statement are the two most common causes of rejection since HUD withdrew its guidance in September 2025.",
      },
      {
        q: "Can any doctor write an ESA letter?",
        a: "Any clinician licensed in your state and working within their scope of practice may write one, including a primary care physician — but housing providers scrutinise documentation from clinicians with no mental health involvement more closely, and many physicians decline. A licensed mental health professional (an LCSW, LMFT, LPC, psychologist or psychiatrist) remains the strongest source. In California, Montana and Arkansas the clinician must also have had a client relationship with you for at least 30 days before issuing the letter; Florida requires a valid assessment and, for an out-of-state practitioner, in-person care on at least one occasion.",
      },
      {
        q: "Is a telehealth ESA letter legally valid?",
        a: "Yes, provided it results from a genuine clinical evaluation — not a questionnaire. HUD recognized telehealth as an appropriate evaluation method before withdrawing its guidance in September 2025, and courts have continued to treat telehealth-based letters as valid when the clinician is licensed in the patient's state and conducted a real assessment. Florida's Stat. §760.27 requires a valid assessment and prohibits letters issued without any live evaluation.",
      },
      {
        q: "How long is an ESA letter valid?",
        a: "No federal law sets an expiration date for ESA documentation. What creates the twelve-month convention is housing-provider practice: most property managers want documentation dated within the past year to show the need is current. Arkansas goes further and requires the provider to review the need annually by statute. Renewing each year — particularly before a lease renewal or a move — keeps a landlord from challenging your documentation on currency grounds.",
      },
      {
        q: "What happens if my ESA letter is rejected?",
        a: "First, determine the reason. If the landlord claims a missing element, ask for clarification in writing and give them the chance to specify what they need. If the letter is complete and the denial appears discriminatory, then — because HUD's federal enforcement office shifted toward trained assistance animals in May 2026 — an effective first step is often a complaint with your state human-rights or fair-housing agency (for example, the California Civil Rights Department or the Florida Commission on Human Relations), which enforce laws that still protect untrained ESAs independently of HUD. Your private litigation rights under 42 U.S.C. §3604(f) also remain intact; a fair-housing attorney can advise. Document everything from the start.",
      },
      {
        q: "Can a landlord charge a pet deposit for an ESA even with a valid letter?",
        a: "This area is now legally contested. Historically, a valid ESA letter waived pet-related fees. However, the July 2025 Henderson v. Five Properties LLC ruling found that a Louisiana landlord could apply a generally applicable animal fee to an ESA owner because the tenant could not prove that waiving the fee was necessary for equal use and enjoyment of the housing. That ruling is jurisdiction-specific and does not change the law nationally, but it signals the automatic fee-waiver assumption is under challenge. State laws in California, New York, Florida and others still offer stronger fee protections.",
      },
      {
        q: "Does my emotional support animal need a vest, an ID card or a registration number?",
        a: "No. There is no government register of emotional support animals, and no vest, tag, certificate or registration number carries legal weight. A registration number printed on a letter is a warning sign to an experienced property manager rather than a credential, because it signals the document came from a product rather than from a clinician who evaluated you.",
      },
      {
        q: "Can one letter cover more than one emotional support animal?",
        a: "It can, but the documentation has to do more work. A clinician who supports two animals should say why each one is needed rather than listing them together, because a housing provider is entitled to ask how the second animal relates to the disability. Vague multi-animal letters are a common source of disputes.",
      },
    ],
  },

  "/blog/how-to-request-esa-accommodation-from-landlord": {
    route: "/blog/how-to-request-esa-accommodation-from-landlord",
    h1: "How to Request an ESA Accommodation From Your Landlord",
    metaTitle: "Requesting an ESA Accommodation From Your Landlord",
    metaDescription:
      "How to submit an emotional support animal accommodation request: what to send, how to word it, what a landlord may ask, and what to do if you are refused.",
    breadcrumb: "Requesting an ESA Accommodation",
    headline: "How to Request an ESA Accommodation From Your Landlord",
    schemaDescription:
      "How to make a reasonable accommodation request for an emotional support animal: the documentation to attach, a wording template you can adapt, what a housing provider may and may not ask, the lawful grounds for refusal, and where to escalate a denial after HUD's 2026 enforcement change.",
    heroImage: "/assets/blog/esa-cluster/esa-accommodation-request-landlord-1200x630.webp",
    heroAlt:
      "Tenant sitting outside an apartment building with a large white dog beside her",
    heroWidth: HERO_W,
    heroHeight: HERO_H,
    datePublished: "2026-09-22",
    dateModified: "2026-09-22",
    articleSection: "Housing Rights",
    keywords: [
      "how to request an ESA accommodation from a landlord",
      "ESA letter to landlord",
      "reasonable accommodation request emotional support animal",
      "emotional support animal housing letter",
      "ESA accommodation request template",
    ],
    faqs: [
      {
        q: "How do I ask my landlord for an emotional support animal accommodation?",
        a: "Put it in writing and keep a copy. Send a short message that says you are requesting a reasonable accommodation under the Fair Housing Act, identifies the animal, and attaches documentation from a clinician licensed in your state. Email or certified mail is better than a conversation, because the date you asked is often the fact that matters later. Ask for a written response and a reasonable deadline.",
      },
      {
        q: "When should I submit the request — before or after signing the lease?",
        a: "You may request an accommodation at any time: while applying, after signing, or after the animal is already living with you. Asking before the animal moves in is generally the cleanest sequence, because it avoids an unauthorised-animal notice while the request is being considered. Some tenants prefer to apply for the unit first and request afterwards; that is lawful, but it is a judgement call rather than a legal requirement, and it does nothing to shorten the landlord's review.",
      },
      {
        q: "What can a landlord ask me for?",
        a: "Where the disability or the need for the animal is not obvious, a housing provider may ask for reliable information that supports both. They may confirm that the clinician wrote the letter and holds an active licence. They may not demand your medical records, your diagnosis, or the severity of your condition, and they may not insist you use their own proprietary medical form. Montana writes the diagnosis limit directly into its landlord-tenant code.",
      },
      {
        q: "Can a landlord still refuse after I send a valid letter?",
        a: "Yes, in defined situations. The property may be exempt from the Fair Housing Act — an owner-occupied building of four units or fewer where the owner lives in one of them, or a single-family home rented by an owner who owns no more than three such houses and uses no broker. A housing provider may also refuse where the particular animal would be a direct threat to the health or safety of others, or would cause substantial physical damage to others' property, and no other accommodation would reduce that risk. Breed, size and a general no-pets policy are not lawful grounds on their own under most state assistance-animal laws.",
      },
      {
        q: "Am I still exempt from pet rent and pet deposits?",
        a: "Assistance animals have historically been treated as outside pet-fee policies, and several state laws still say so directly — Florida's statute, for example, provides that a person with a disability-related need for an emotional support animal may not be required to pay extra compensation for it. Federally the position is less settled than it was: a 2025 federal district-court ruling allowed a generally applicable animal fee where the tenant could not show a waiver was necessary, and HUD's May 2026 enforcement memorandum narrowed the complaints its own office will pursue. Check your state and city rules, and never assume a waiver is automatic.",
      },
      {
        q: "What do I do if my request is denied?",
        a: "Ask for the reason in writing. If it points to a missing element, supply it. If the denial looks unlawful, your state or local fair-housing or human-rights agency is now often the more productive route than a federal complaint, because HUD's enforcement office said in May 2026 that it will find reasonable cause only where the animal is individually trained to perform disability-related tasks. Your right to bring a private Fair Housing Act suit is unchanged, and a fair-housing attorney or a local tenants' organisation can tell you which path fits your state.",
      },
      {
        q: "Does an accommodation mean I am not responsible for damage?",
        a: "No. An accommodation removes pet-policy barriers; it does not shift the cost of harm. You remain responsible for damage the animal causes beyond normal wear and tear, and for the animal's behaviour in shared spaces. Noise and damage complaints are among the most common ways an approved accommodation later unravels.",
      },
    ],
  },

  "/blog/common-reasons-esa-letters-get-rejected": {
    route: "/blog/common-reasons-esa-letters-get-rejected",
    h1: "Common Reasons ESA Letters Get Rejected",
    metaTitle: "Common Reasons ESA Letters Get Rejected | PawTenant",
    metaDescription:
      "Why landlords turn down emotional support animal documentation: missing elements, instant certificates, state-licence mismatches, and how to fix each one.",
    breadcrumb: "Why ESA Letters Get Rejected",
    headline: "Common Reasons ESA Letters Get Rejected",
    schemaDescription:
      "The reasons housing providers reject emotional support animal documentation — unverifiable clinicians, instant online certificates, missing licence details, stale dates, state-licence mismatches and vague wording — together with the lawful and unlawful grounds a landlord may rely on.",
    heroImage: "/assets/blog/esa-cluster/esa-letter-rejected-reasons-1200x630.webp",
    heroAlt:
      "Person reviewing housing paperwork on a laptop outdoors with a small terrier sitting beside them",
    heroWidth: HERO_W,
    heroHeight: HERO_H,
    datePublished: "2026-09-22",
    dateModified: "2026-09-22",
    articleSection: "ESA Letters",
    keywords: [
      "why ESA letters get rejected",
      "landlord rejected ESA letter",
      "ESA letter denied",
      "valid ESA letter",
      "ESA letter to landlord",
    ],
    faqs: [
      {
        q: "What is the single most common reason an ESA letter is rejected?",
        a: "Documentation a housing provider cannot verify. In practice that usually means a missing licence number or state of licensure, so the property manager has no way to confirm the author is a licensed clinician at all. It is the fastest rejection to earn and the easiest to prevent — check those two fields before you send anything.",
      },
      {
        q: "Why do landlords reject letters from instant online ESA services?",
        a: "Because a certificate produced minutes after a questionnaire is evidence of a purchase, not of an evaluation. Property managers see the same templates repeatedly and have learned to recognise them, and documentation bought online without a real assessment has long been treated as unreliable on its own. Telehealth is not the problem; the absence of a clinician who actually assessed you is.",
      },
      {
        q: "Can a landlord reject my letter for being more than a year old?",
        a: "Often, yes — not because a federal expiry date exists, but because a housing provider may ask for information showing that the need is current. A letter dated three years ago does not show that. Arkansas turns this into a provider duty by statute, requiring an annual review of the need. Renewing before a lease renewal or a move is the simplest way to remove the argument.",
      },
      {
        q: "My therapist is real and licensed. Why was the letter still refused?",
        a: "Usually because the wording is missing a required link rather than because the clinician is doubted. Two gaps account for most of these: the letter never states that you have a disability as fair-housing law defines it, or it never explains how this particular animal relates to it. \"The patient would benefit from a pet\" is the classic failing sentence. A letter addressed to no one, with no practice contact details, invites the same result.",
      },
      {
        q: "Which reasons for refusing an ESA are not legitimate?",
        a: "A blanket no-pets policy, a breed the landlord dislikes, a weight limit, a demand for your specific diagnosis, a requirement that you use the property's own medical form, and a demand for a registration or certification number all fall outside what assistance-animal rules contemplate. Lawful grounds are narrower: a statutory exemption for the property, an animal that is a direct threat to health or safety, substantial physical damage to others' property, or documentation that genuinely cannot be verified.",
      },
      {
        q: "Does HUD's 2026 enforcement change mean my letter is worthless?",
        a: "No, but it changes where pressure comes from. HUD's May 2026 memorandum tells its own enforcement office to find reasonable cause only where the animal is individually trained to perform disability-related tasks. It did not amend the Fair Housing Act, it does not bind courts, it preserves your right to sue privately, and it expressly leaves state and local law alone. Many landlords have not changed their day-to-day practice. Strong documentation and your state's rules both matter more than they did.",
      },
      {
        q: "What should I do the moment a letter is rejected?",
        a: "Ask for the reason in writing, and ask for it to be specific. A written reason tells you whether this is a fixable paperwork gap or a refusal you may need to escalate, and it becomes part of the record if you later contact a state fair-housing agency or a lawyer. Do not remove the animal or agree to pet fees before you understand which of the two you are dealing with.",
      },
    ],
  },

  "/blog/when-should-you-renew-your-esa-letter": {
    route: "/blog/when-should-you-renew-your-esa-letter",
    h1: "When Should You Renew Your ESA Letter?",
    metaTitle: "When to Renew Your ESA Letter | PawTenant",
    metaDescription:
      "How long ESA documentation stays current, the five events that should trigger a renewal, and why twelve months is landlord practice, not federal law.",
    breadcrumb: "When to Renew Your ESA Letter",
    headline: "When Should You Renew Your ESA Letter?",
    schemaDescription:
      "When emotional support animal documentation should be refreshed: the twelve-month recency convention and where it actually comes from, the five renewal triggers, the state rules that add their own timing, and what it costs to let documentation go stale.",
    heroImage: "/assets/blog/esa-cluster/esa-letter-renewal-timing-1200x630.webp",
    heroAlt:
      "Tenant at home on a sofa with a laptop and a brown dog, reviewing housing documents",
    heroWidth: HERO_W,
    heroHeight: HERO_H,
    datePublished: "2026-09-22",
    dateModified: "2026-09-22",
    articleSection: "ESA Letters",
    keywords: [
      "when to renew an ESA letter",
      "how long is an ESA letter valid",
      "ESA letter renewal",
      "ESA documentation expiration",
      "renew ESA letter for housing",
    ],
    faqs: [
      {
        q: "How long does an ESA letter stay valid?",
        a: "There is no federal expiry date. What most people are describing when they say a letter \"expires after a year\" is a housing-provider convention: property managers commonly ask for documentation dated within the past twelve months, because a recent letter shows the need is current. Treat twelve months as the point at which a landlord may reasonably ask for something newer, not as a date on which your documentation stops existing.",
      },
      {
        q: "Does federal law say an ESA letter expires after 12 months?",
        a: "No. No provision of the Fair Housing Act or its regulations sets a validity period for assistance-animal documentation. The twelve-month figure comes from property-management practice, and — in Arkansas — from a state statute that requires the provider to review the need for the animal annually. Any source telling you that federal law imposes a universal one-year expiry is repeating a myth.",
      },
      {
        q: "When should I actually start the renewal?",
        a: "About a month to six weeks before you expect to need current documentation, and earlier in a state with a relationship requirement. Booking an appointment, being evaluated and receiving a signed letter takes time, and in California, Montana and Arkansas a clinician cannot issue documentation until a client relationship has existed for at least 30 days. Starting at the eleven-month mark leaves room for both.",
      },
      {
        q: "Do I need a new letter if I move to a different state?",
        a: "Usually, yes — though not because the old letter self-destructs. Clinical licensure is state-based, so the clinician who evaluated you may not be licensed where you now live, and a new housing provider will often notice. Some states make this explicit: Florida accepts documentation from an out-of-state practitioner only if that practitioner has provided in-person care on at least one occasion. Plan on establishing care with a clinician licensed in your new state.",
      },
      {
        q: "What happens if my documentation lapses?",
        a: "Nothing automatic, but you lose the easy answer. In a building with a pet policy, a landlord who asks for current documentation and does not receive it may start treating the animal as a pet — pet rent, a deposit, or in a no-pets building a notice to cure. Fees charged while your documentation was out of date are not usually refunded afterwards. Renewing before the request arrives avoids the entire argument.",
      },
      {
        q: "Can a different clinician renew my letter?",
        a: "Yes. Any clinician licensed in your state and working within their scope of practice can evaluate you and issue new documentation; it does not have to be the person who wrote the first letter. If you are starting with someone new in a state with a relationship requirement, allow for that waiting period.",
      },
      {
        q: "Should I renew before a lease renewal even if nobody has asked?",
        a: "It is usually worth it. Lease renewals and move-ins are the two moments when assistance-animal files get audited, and arriving with current documentation is far easier than responding to a request under a deadline. It is also the cheapest time to correct a letter that turns out to be missing a licence number or a clear statement linking the animal to your disability.",
      },
    ],
  },

  "/blog/can-puppies-qualify-as-esas": {
    route: "/blog/can-puppies-qualify-as-esas",
    h1: "Can Puppies Qualify as Emotional Support Animals?",
    metaTitle: "Can You Have a Puppy as an ESA? | PawTenant",
    metaDescription:
      "Can you have a puppy as an ESA? There's no minimum age, but puppies bring real care and tenancy risks. What the rules say and what clinicians weigh.",
    breadcrumb: "Can Puppies Qualify as ESAs?",
    headline: "Can Puppies Qualify as Emotional Support Animals?",
    schemaDescription:
      "Whether a puppy can be an emotional support animal: the absence of any minimum age in fair-housing law, what a clinician weighs before supporting a request, the housing rules that still apply, and the behaviour and vaccination issues that put a young dog's accommodation at risk.",
    heroImage: "/assets/blog/esa-cluster/puppy-emotional-support-animal-1200x630.webp",
    heroAlt:
      "Renter holding up a small black puppy outside an apartment building",
    heroWidth: HERO_W,
    heroHeight: HERO_H,
    datePublished: "2026-09-22",
    dateModified: "2026-09-22",
    articleSection: "ESA Letters",
    keywords: [
      "can a puppy be an emotional support animal",
      "puppy ESA age requirement",
      "emotional support animal dog",
      "ESA letter for a dog",
      "puppy emotional support animal housing",
    ],
    faqs: [
      {
        q: "Is there a minimum age for an emotional support animal?",
        a: "No. Neither the Fair Housing Act nor its regulations set an age floor for an assistance animal, and no state ESA statute imposes one either. An eight-week-old puppy is not excluded by age. What a clinician has to be able to say is that you have a disability and that this animal relates to it — and that judgement does not turn on the animal's birthday.",
      },
      {
        q: "Will a clinician support an ESA request for a puppy?",
        a: "Sometimes, and not automatically. A careful clinician weighs both directions. A young dog can create daily structure and reliable companionship. It can also cost you months of broken sleep, which is a well-recognised destabiliser for depression, panic and bipolar disorder, and the noise and mess of house-training can be hard on someone with low frustration tolerance. Expect to be asked how you will manage the first six months, not only why you want the animal.",
      },
      {
        q: "Can a landlord charge a pet deposit because the puppy is not trained yet?",
        a: "Training has never been part of what makes an animal an emotional support animal, so a landlord cannot treat an untrained young dog differently from an untrained adult one on that basis. Whether any animal fee may be charged at all is a separate and now less settled question: several state laws still bar extra compensation for an assistance animal, while federal enforcement narrowed in May 2026. Your state rules are the ones to check.",
      },
      {
        q: "Can my landlord still require vaccination and licensing records?",
        a: "Yes. Local public-health and animal-control rules apply to every animal in the building, including an assistance animal. Rabies vaccination and municipal dog licensing typically begin around twelve to sixteen weeks, and a housing provider may ask for proof once the requirement applies. This is not a disability question and refusing to supply it is a poor hill to choose.",
      },
      {
        q: "Can an accommodation be withdrawn because a puppy barks or chews?",
        a: "It can. An accommodation removes pet-policy barriers; it does not suspend the lease. Sustained barking that disturbs neighbours after written warnings, or ongoing damage to the unit, are the ordinary grounds a housing provider relies on — and you remain financially responsible for repairs regardless of any fee waiver. Crate training and early socialisation protect the accommodation as much as they protect the furniture.",
      },
      {
        q: "Does HUD's May 2026 change affect a puppy specifically?",
        a: "It affects every untrained emotional support animal in the same way, and a puppy is by definition untrained. HUD's enforcement office said it will find reasonable cause only where an animal is individually trained to perform disability-related tasks, so a federal complaint about an untrained young dog is unlikely to be pursued. The statute is unchanged, private lawsuits remain available, and state and local assistance-animal laws are untouched — which is where the practical protection now sits.",
      },
      {
        q: "Can I train my ESA puppy into a psychiatric service dog later?",
        a: "Many people do. A psychiatric service dog is a dog individually trained to perform specific tasks for a psychiatric disability, and it carries public-access rights and cabin travel that an emotional support animal does not. Training takes time and reliability in public, so it is a path rather than an upgrade you can buy — but starting with a young dog is a reasonable place to start it.",
      },
    ],
  },
};

/** Ordered list — used by the guard, the prerender entry and the cluster nav. */
export const ESA_LETTER_CLUSTER_ROUTES: string[] = Object.keys(ESA_LETTER_CLUSTER);

/** The five routes this task created. /blog/esa-letter-requirements already existed. */
export const ESA_LETTER_CLUSTER_NEW_ROUTES: string[] = ESA_LETTER_CLUSTER_ROUTES.filter(
  (r) => r !== "/blog/esa-letter-requirements",
);

export function clusterArticle(route: string): ClusterArticle | undefined {
  return ESA_LETTER_CLUSTER[route];
}

/**
 * The full @graph for one cluster article: BlogPosting + BreadcrumbList +
 * FAQPage, built from the record above so the schema cannot describe copy the
 * page does not render. Returns null for any route outside the cluster.
 */
export function clusterArticleGraph(route: string): Record<string, unknown> | null {
  const a = ESA_LETTER_CLUSTER[route];
  if (!a) return null;
  const canonical = `${BASE_URL}${a.route}`;
  const image = `${BASE_URL}${a.heroImage}`;
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BlogPosting",
        "@id": `${canonical}#article`,
        headline: a.headline,
        description: a.schemaDescription,
        mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
        url: canonical,
        image: [image],
        datePublished: a.datePublished,
        dateModified: a.dateModified,
        author: { "@type": "Organization", name: "PawTenant", url: `${BASE_URL}/` },
        publisher: { "@type": "Organization", name: "PawTenant", url: `${BASE_URL}/` },
        articleSection: a.articleSection,
        keywords: a.keywords.join(", "),
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${BASE_URL}/` },
          { "@type": "ListItem", position: 2, name: "Blog", item: `${BASE_URL}/blog` },
          { "@type": "ListItem", position: 3, name: a.breadcrumb, item: canonical },
        ],
      },
      {
        "@type": "FAQPage",
        mainEntity: a.faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
  };
}

/**
 * Marker attribute on the <head> script the build-time generator injects. The
 * client component looks for it and emits nothing when it is present, so a
 * hydrated page carries exactly one copy of each entity.
 */
export const CLUSTER_SSR_ATTR = "data-pt-article-ssr";
